import React from "react";
import type { ZudokuConfig } from "zudoku";
import APILogsPage from "./src/api_logs/APILogsPage";
import ApplicationsPage from "./src/applications/ApplicationsPage";
import ApplicationAPIAccessSettings from "./src/applications/ApplicationAPIAccessSettingsPage";
import TenantSwitcher from "./src/tenant_switcher/TenantSwitcher";
import APIAnalyticsPage from "./src/api_analytics/APIAnalyticsPage";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { MsalProvider } from "@azure/msal-react";
import { msalInstance } from "./src/hooks/msalInstance";

// ⚠️ Ensure MSAL is initialized before rendering anything
await msalInstance.initialize();

// Shared wrapper
const queryClient = new QueryClient();
const withProviders = (component: React.ReactNode) => (
  <MsalProvider instance={msalInstance}>
    <QueryClientProvider client={queryClient}>{component}</QueryClientProvider>
  </MsalProvider>
);

const config: ZudokuConfig = {
  port: 4001,
  https: {
    key: "./localhost-key.pem",
    cert: "./localhost.pem",
  },
  slots: {
    "head-navigation-start": () => withProviders(<TenantSwitcher />),
  },
  site: {
    logo: {
      src: {
        light: "/MyLogo.png",
        dark: "/MyLogo.png"
      },
      alt: "Neighborly Software Logo",
    }
  },
  metadata: {
    logo: "/MyLogo.png",
    favicon: "/neighborly-icon.png",
    title: process.env.ZUDOKU_PUBLIC_PAGE_TITLE || "Neighborly Software",
  },
  apis: {
    type: "file",
    input: "./apis/openapi.yaml",
    path: "/api",
  },
  defaults: {
    apis: {
      disablePlayground: true,
    },
  },
  theme: {
    customCss: {
      main: {
        "z-index": "8",
      },
    },
  },
  authentication: {
    type: "azureb2c",
    issuer: process.env.ZUDOKU_PUBLIC_AZURE_B2C_ISSUER,
    clientId: process.env.ZUDOKU_PUBLIC_AZURE_B2C_CLIENT_ID,
    tenantName: process.env.ZUDOKU_PUBLIC_AZURE_B2C_TENANT_NAME,
    policyName: process.env.ZUDOKU_PUBLIC_AZURE_B2C_POLICY_NAME,
    scopes: ["openid", "profile", "email", "offline_access"],
  },
  page: {
    logo: {
      src: {
        light: "/MyLogo.png",
        dark: "/MyLogo.png",
      },
      alt: "Neighborly Software Logo",
    },
  },
  docs: {
    files: "/pages/**/*.{md,mdx}",
  },
  protectedRoutes: ["/logs/*", "/applications/*", "/analytics/*"],
  navigation: [
    {
      type: "category",
      label: "Documentation",
      icon: "book",
      items: [
        {
          type: "link",
          to: "/docs/introduction",
          label: "Introduction",
          icon: "info",
          description: "Overview of the Neighborly Software API",
        },
        {
          type: "link",
          to: "/docs/security",
          label: "Security",
          icon: "shield",
          description: "Overview of the Neighborly Software API security features",
        },
        {
          type: "link",
          to: "/docs/errors",
          label: "Errors",
          icon: "alert-triangle",
          description: "Overview of the Neighborly Software API error handling",
        },
        {
          type: "link",
          to: "/docs/sandbox",
          label: "Sandbox",
          icon: "codesandbox",
          description: "Overview of the Neighborly Software API sandbox environment",
        },
      ],
    },
    {
      type: "link",
      to: "/api",
      label: "API Reference",
      icon: "code",
      description: "Complete API documentation",
      display: "always",
    },
    {
      type: "custom-page",
      path: "/logs",
      element: withProviders(<APILogsPage />),
      label: "API Logs",
      icon: "activity",
      display: "auth",
    },
    {
      type: "custom-page",
      path: "/analytics",
      element: withProviders(<APIAnalyticsPage />),
      label: "API Analytics",
      icon: "bar-chart",
      display: "auth",
    },
    {
      type: "custom-page",
      path: "/applications",
      element: withProviders(<ApplicationsPage />),
      label: "Applications",
      icon: "grid",
      display: "auth",
    },
    {
      type: "custom-page",
      path: "/applications/:tenantCode/:applicationId",
      element: withProviders(<ApplicationAPIAccessSettings />),
      display: "hide",
    },
  ],
};

export default config;
